/**
 * ขั้นที่ 3 — สแกนออก (Scan-Out) ปิดรอบงานพร้อมสามจำนวน
 *
 * นี่คือหน้าจอที่ "2 scan + 3 ช่อง" พูดถึง: ผู้ปฏิบัติงานระบุว่ากำลังปิดงานไหน
 * แล้วกรอก **`qty_good` / `qty_scrap` / `qty_rework` เท่านั้น** ไม่มีช่องที่สี่
 * (`references/shop-floor-pull-traceability.md` — "Production cycle ต้องเหลือแค่
 * scan 2 ครั้ง + กรอกตัวเลข 3 ช่อง") การเพิ่มฟอร์มบังคับที่นี่คือการทำให้คนหน้างาน
 * ข้ามขั้นตอน แล้ว traceability ขาดช่วง
 *
 * ไม่ POST: ค่าที่กรอกกลายเป็นคำสั่ง `SCAN_OUT` หนึ่งใบในคิวออฟไลน์ (D-075)
 * การ backflush, สร้าง output lot, genealogy, เอกสารบัญชีและ outbox ทั้งหมดเกิดใน
 * ธุรกรรมเดียวของเซิร์ฟเวอร์ตอนซิงค์ (D-066..D-070) — เครื่องหน้างานไม่คำนวณ
 * ยอดคงเหลือหรือมูลค่าใด ๆ และห้ามคำนวณ (`docs/architecture.md` Dependency Rule 8)
 *
 * การตรวจสองข้อที่หน้าจอทำ เป็น **เงา** ของด่านฝั่งเซิร์ฟเวอร์ ไม่ใช่ความจริงชุด
 * ที่สอง: ค่าติดลบ (contract รับเฉพาะทศนิยม ≥ 0) และทั้งสามเป็นศูนย์หมด
 * (`422 NOTHING_CONFIRMED`) — ปฏิเสธที่นี่เพื่อไม่ให้คำสั่งที่รู้อยู่แล้วว่าจะถูก
 * ปฏิเสธไปนอนในคิวจนกว่าจะซิงค์ได้ แล้วเด้งกลับมาเป็น conflict ให้ตามแก้ทีหลัง
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ScanCommandForm, ScanField, type ScanScreenProps } from './ScanCommandForm';
import { useScanSubmit, type ScanCommandDraft } from './useScanSubmit';
import { SCAN_OUT } from '../lib/shopFloor/commands';
import {
  confirmsNothing,
  isNonNegativeDecimal,
  isPositiveInteger,
} from '../lib/shopFloor/quantities';

/** รูปรหัสป้าย Kanban การผลิต — `OUT-` + เลข 8 หลัก (D-170, REV-009 F1) */
const OUTPUT_TAG_CODE_PATTERN = /^OUT-[0-9]{8}$/;

export function ScanOutScreen({ pendingSync }: ScanScreenProps) {
  /*
   * ค่าเริ่มต้นจาก TagScanScreen (`?order=<uuid>&seq=<n>`, D-170) — อ่านครั้งแรก
   * ที่ mount เท่านั้น ผู้ใช้แก้ไขต่อได้ตามปกติ ไม่มี param = พฤติกรรมเดิมทุกประการ
   */
  const [searchParams] = useSearchParams();
  const [plantCode, setPlantCode] = useState('');
  const [productionOrderId, setProductionOrderId] = useState(() => searchParams.get('order') ?? '');
  const [operationSeq, setOperationSeq] = useState(() => searchParams.get('seq') ?? '');
  const [outputTagCode, setOutputTagCode] = useState(() => searchParams.get('tag') ?? '');
  const [qtyGood, setQtyGood] = useState('');
  const [qtyScrap, setQtyScrap] = useState('');
  const [qtyRework, setQtyRework] = useState('');
  const { state, submit } = useScanSubmit();

  const buildDraft = (): ScanCommandDraft => {
    const problems: string[] = [];
    const plant = plantCode.trim();
    const order = productionOrderId.trim();
    const seq = operationSeq.trim();
    // ⚠️ ไม่ trim ค่า NUMERIC: สิ่งที่พิมพ์ต้องถึงเซิร์ฟเวอร์เหมือนเดิมทุกตัวอักษร

    if (plant === '') {
      problems.push('ต้องระบุรหัสโรงงาน — ทุกคำสั่งเป็นของโรงงานใดโรงงานหนึ่งเสมอ (D-004)');
    }
    if (order === '') {
      problems.push('ต้องระบุใบสั่งผลิตที่กำลังปิดรอบงาน');
    }
    if (!isPositiveInteger(seq)) {
      problems.push('ลำดับขั้นตอน (operation_seq) ต้องเป็นจำนวนเต็มมากกว่า 0');
    }

    /*
     * ทั้งสามช่องต้องมีค่าเสมอ — ช่องว่างไม่ใช่ศูนย์ ถ้าไม่มีของเสียต้องพิมพ์ 0
     * ให้เห็นกับตา การเดาแทนผู้ปฏิบัติงานว่าช่องที่เว้นไว้คือศูนย์ คือการบันทึก
     * ตัวเลขผลิตที่ไม่มีใครเคยยืนยัน
     */
    for (const [label, value] of [
      ['จำนวนดี (qty_good)', qtyGood],
      ['จำนวนเสีย (qty_scrap)', qtyScrap],
      ['จำนวนแก้ไข (qty_rework)', qtyRework],
    ] as const) {
      if (value.trim() === '') {
        problems.push(`ต้องกรอก${label} — ถ้าไม่มีให้ใส่ 0`);
      } else if (!isNonNegativeDecimal(value)) {
        problems.push(`${label} ต้องเป็นตัวเลขไม่ติดลบ`);
      }
    }

    if (problems.length === 0 && confirmsNothing(qtyGood, qtyScrap, qtyRework)) {
      problems.push(
        'ทั้งสามจำนวนเป็นศูนย์ทั้งหมด — การปิดรอบงานที่ไม่ได้ผลิตอะไรเลยไม่ใช่การปิดงาน',
      );
    }

    /*
     * ไม่บังคับในฟอร์ม (เซิร์ฟเวอร์เป็นผู้ตัดสินตอน sync ว่า operation นี้ต้องมี
     * ป้ายหรือไม่) แต่ถ้ากรอกมาต้องตรงรูปก่อนเข้าคิว — รหัสผิดรูปไม่ใช่ป้ายจริง
     */
    const tag = outputTagCode.trim();
    if (tag !== '' && !OUTPUT_TAG_CODE_PATTERN.test(tag)) {
      problems.push('รหัสป้าย Kanban ต้องอยู่ในรูป OUT- ตามด้วยเลข 8 หลัก');
    }

    if (problems.length > 0) {
      return { ok: false, problems };
    }

    return {
      ok: true,
      command: {
        command_type: SCAN_OUT,
        payload: {
          plant_code: plant,
          production_order_id: order,
          operation_seq: Number(seq),
          qty_good: qtyGood,
          qty_scrap: qtyScrap,
          qty_rework: qtyRework,
          ...(tag === '' ? {} : { output_tag_code: tag }),
        },
      },
    };
  };

  const handleSubmit = async () => {
    const result = await submit(buildDraft());
    if (result?.status === 'queued') {
      /* จำนวนของรอบถัดไปเป็นคนละชุดเสมอ ค้างไว้คือความเสี่ยงที่จะถูกส่งซ้ำ */
      setProductionOrderId('');
      setOperationSeq('');
      setOutputTagCode('');
      setQtyGood('');
      setQtyScrap('');
      setQtyRework('');
    }
  };

  return (
    <ScanCommandForm
      testId="scan-out-screen"
      title="ปิดรอบงานและบันทึกสามจำนวน"
      hint="กรอกสามจำนวนนี้เท่านั้น — เซิร์ฟเวอร์เป็นผู้ตัดสต๊อกตาม lot ที่จัดเตรียมไว้ สร้าง lot ใหม่ ผูกสายพันธุ์ และลงบัญชีในธุรกรรมเดียวตอนซิงค์"
      submitLabel="เข้าคิวการสแกนออก"
      state={state}
      pendingSync={pendingSync}
      onSubmit={() => {
        void handleSubmit();
      }}
    >
      <ScanField
        label="รหัสโรงงาน (plant_code)"
        name="plant_code"
        testId="scan-out-plant"
        value={plantCode}
        onChange={setPlantCode}
        placeholder="เช่น BKK-01"
        hint="ยังต้องกรอกเองจนกว่าอุปกรณ์จะถูกลงทะเบียนผูกกับโรงงาน/ไลน์"
      />
      <ScanField
        label="ใบสั่งผลิต (production_order_id)"
        name="production_order_id"
        testId="scan-out-order"
        value={productionOrderId}
        onChange={setProductionOrderId}
      />
      <ScanField
        label="ลำดับขั้นตอน (operation_seq)"
        name="operation_seq"
        testId="scan-out-operation"
        value={operationSeq}
        onChange={setOperationSeq}
        inputMode="numeric"
        placeholder="เช่น 20"
      />
      <ScanField
        label="รหัสป้าย Kanban (OUT-…)"
        name="output_tag_code"
        testId="scan-out-tag"
        value={outputTagCode}
        onChange={setOutputTagCode}
        placeholder="เช่น OUT-00000012"
        hint="ถ้าใบสั่งนี้พิมพ์ป้ายไว้ ต้องใส่รหัสป้ายของแพ็คเกจที่เพิ่งผลิตเสร็จ"
      />
      <ScanField
        label="จำนวนดี (qty_good)"
        name="qty_good"
        testId="scan-out-qty-good"
        value={qtyGood}
        onChange={setQtyGood}
        inputMode="decimal"
        placeholder="เช่น 48.0000"
      />
      <ScanField
        label="จำนวนเสีย (qty_scrap)"
        name="qty_scrap"
        testId="scan-out-qty-scrap"
        value={qtyScrap}
        onChange={setQtyScrap}
        inputMode="decimal"
        placeholder="เช่น 2.0000"
      />
      <ScanField
        label="จำนวนแก้ไข (qty_rework)"
        name="qty_rework"
        testId="scan-out-qty-rework"
        value={qtyRework}
        onChange={setQtyRework}
        inputMode="decimal"
        placeholder="เช่น 0.0000"
        hint="ไม่มีของเสีย/ของแก้ไข ให้ใส่ 0 — ช่องว่างไม่ถูกตีความว่าเป็นศูนย์"
      />
    </ScanCommandForm>
  );
}
